import { FLAG_ROLLOUT_CHARACTERS, FLAG_ROLLOUT_CODEX, FLAG_ROLLOUT_CONSOLE } from './EsFlags';
import { ES_PALETTE, ES_SHADES } from './EsTailwindPalette';

/**
 * Every color a person can change, in the order Colors lists them.
 *
 * EsCommon.css IS the truth for the built-in values -- `@theme static` for light,
 * the unlayered `:where(.dark, .dark *)` block for dark -- and this table is its
 * mirror, because the editor has to show a default it cannot read back out of a
 * stylesheet (jsdom resolves no var() chain, and a browser hands back oklch).
 * EsThemeTokens.test.jsx parses EsCommon.css and fails on any disagreement, and
 * checks the names against esback's EsThemeTokens.java, which refuses a token it
 * does not know. Add a token in all three places or the build says so.
 *
 * A built-in value is a hex (`#faf4ed`, or `#rrggbbaa` with alpha) or a
 * Tailwind step by name (`blue-700`, `white`). What a person SAVES is always hex -- see esAsHex -- so esback needs no palette.
 *
 * TWO LAYERS (Tim, 2026-10-24: fewer colors to change, and plain what changes
 * what). The SEMANTIC layer comes first, named after PrimeReact 11's tokens: a
 * panel of twelve colors that hold their own values (the page, the card, three
 * inks, the border, the primary, and the five severities), the colors derived
 * from them (a hover, a wash, a divider, a tint per severity), and eight
 * categorical hues for dice, slots, and series. Every COMPONENT token below it
 * keys to that layer, so picking `primary-color` moves links, focus, the tab bar,
 * and the rest at once, and a sheet that states only the panel is a whole look.
 * `field` alone keeps a value of its own: clear, which no role owns.
 *
 * A KEY: `same: 'accent'` in place of light and dark says this token paints
 * whatever `accent` paints, in both modes. `accent/60` is the same at 60% alpha,
 * `surface>5` the base mixed 5% toward black, `surface<5` 5% toward white, and
 * `danger+warn` the two bases half and half -- how orange is made of red and
 * amber. `same` may instead be { light, dark }, a key per mode, for a color that
 * moves the other way by night: a hover darker by day is lighter by night, and
 * white ink on a fill wants the fill darker in both. The stylesheet says it the
 * same way -- `var(--color-accent)`, or color-mix of it with transparent, black,
 * white, or the second base -- in the light block, and a dark line only for a
 * key that differs by mode, so a
 * keyed token follows its base at run time too: pin `accent` in a sheet and
 * `link` moves with it, until `link` is given a color of its own. The base is
 * the token whose role the color belongs to. Keys may chain; the test refuses
 * a cycle. The Colors page shows a keyed cell as its key until the cell is
 * picked, and a pick is a hex like any other.
 *
 * EQUINOX (2026-10-24) is the built-in panel: Rosé Pine Dawn by day and Rosé
 * Pine by night, as the Rosé Pine sheet maps them, black ink by day included.
 * The shipped Equinox states that panel and nothing else, and is the default.
 *
 * `ink` marks a token that paints text, and `on` the ground it most often sits
 * on -- esContrast's inputs. Every ink clears ES_CONTRAST_MIN on its ground in
 * both modes, and EsThemeTokens.test.jsx walks the table to say so.
 *
 * `sees` is who Colors shows a group to, by the same checks that show
 * the feature itself: `admin`, `signedIn`, and `flags` (any one of them on). A
 * group with no `sees` is shared by every page and always shows. A person does
 * not recolor what they cannot see; an admin aimed at the site sees every group.
 * `layer: 'semantic'` puts a group under Colors' Semantic heading; the rest are
 * its Component groups.
 */
export const ES_TOKEN_GROUPS = [
    // The Semantic layer: the panel, what it derives, and the categories. Every component
    // token below keys to these, so a sheet that states only this layer is a whole look.
    { name: 'Panel', layer: 'semantic', tokens: [
        { token: 'background', label: 'The page itself', light: '#faf4ed', dark: '#191724' },
        { token: 'content-background', label: 'A card, a sheet, a panel', light: '#fffaf3', dark: '#1f1d2e' },
        { token: 'text-color', label: 'Prose, and most ink', light: '#000000', dark: '#e0def4', ink: true, on: 'background' },
        { token: 'text-muted-color', label: 'Context rather than content', light: '#000000', dark: '#908caa', ink: true, on: 'background' },
        { token: 'heading-color', label: 'Titles of screens, panels, and cards', light: '#000000', dark: '#e0def4', ink: true, on: 'content-background' },
        { token: 'content-border-color', label: 'The edge of a card or panel', light: '#dfdad9', dark: '#403d52' },
        { token: 'primary-color', label: 'The accent: links, the current section, focus', light: '#7b659b', dark: '#a5b4fc', ink: true, on: 'background' },
        { token: 'success', label: 'It worked: a goal met, a roll that passed, unhurt', light: '#427681', dark: '#3b92b7', ink: true, on: 'content-background' },
        { token: 'info', label: 'For the record: notes, info lines', light: '#286983', dark: '#9ccfd8', ink: true, on: 'content-background' },
        { token: 'warn', label: 'Take care: hurt, a notice, whose turn', light: '#9b6111', dark: '#f6c177', ink: true, on: 'content-background' },
        { token: 'help', label: 'An aside: the chat, system lines, alerts', light: '#7e689d', dark: '#c4a7e7', ink: true, on: 'content-background' },
        { token: 'danger', label: 'Something went wrong', light: '#a75269', dark: '#eb6f92', ink: true, on: 'background' },
    ] },
    { name: 'Derived', layer: 'semantic', tokens: [
        { token: 'primary-hover-color', label: 'The accent under the pointer', same: { light: 'primary-color>15', dark: 'primary-color<15' }, ink: true, on: 'background' },
        { token: 'primary-contrast-color', label: 'Ink that reads on the accent', same: 'content-background', ink: true, on: 'primary-color' },
        { token: 'content-hover-background', label: 'A box in a card, a row under the pointer', same: { light: 'content-background>4', dark: 'content-background<6' } },
        { token: 'highlight-background', label: 'The accent as a wash: a picked row, a badge', same: 'primary-color/15' },
        { token: 'overlay-background', label: 'A sheet raised over the page: a menu', same: 'content-background' },
        { token: 'divider-color', label: 'A line inside a panel', same: 'content-border-color+content-background' },
        { token: 'focus-ring-color', label: 'Where the keyboard is', same: 'primary-color' },
        { token: 'success-background', label: 'A success band', same: 'success/15' },
        { token: 'info-background', label: 'An info band', same: 'info/15' },
        { token: 'warn-background', label: 'A warning band: a notice', same: 'warn/15' },
        { token: 'help-background', label: 'An aside band', same: 'help/15' },
        { token: 'danger-background', label: 'A danger band, a delete control under the pointer', same: 'danger/15' },
    ] },
    // Eight hues told apart, for dice, spell slots, and series; mixed from the panel where
    // it has no such hue.
    { name: 'Categorical', layer: 'semantic', tokens: [
        { token: 'chart-1', label: 'Red: the d4, slot 1', same: 'danger' },
        { token: 'chart-2', label: 'Orange: the d6, slot 2, bloodied', same: 'danger+warn' },
        { token: 'chart-3', label: 'Yellow: the d20, slot 3, the hero', same: 'warn' },
        { token: 'chart-4', label: 'Green: the d8, slot 4', same: 'success' },
        { token: 'chart-5', label: 'Blue: the d10, slot 5', same: 'info' },
        { token: 'chart-6', label: 'Violet: the d12, slot 7', same: 'help' },
        { token: 'chart-7', label: 'Magenta: slot 8', same: 'danger+help' },
        { token: 'chart-8', label: 'Neutral: the d100, slot 9', same: 'text-muted-color' },
    ] },
    { name: 'Page', tokens: [
        { token: 'surface', label: 'The page itself', same: 'background' },
        { token: 'on-surface', label: 'Ink the page sets by default', same: 'title', ink: true, on: 'surface' },
        { token: 'panel', label: 'A raised sheet: the mobile menu, back-to-top', same: 'overlay-background' },
        { token: 'panel-hover', label: 'That sheet under the pointer', same: 'content-hover-background' },
        { token: 'card', label: 'A card: sheets, campaign cards, admin tables', same: 'content-background' },
        { token: 'section-rule', label: 'The hairline between bands of the page', same: 'edge' },
    ] },
    { name: 'Text', tokens: [
        { token: 'body', label: 'Prose, and the ink inside a field', same: 'text-color', ink: true, on: 'surface' },
        { token: 'muted', label: 'Context rather than content', same: 'text-muted-color', ink: true, on: 'surface' },
        { token: 'aside', label: 'A unit, a parenthetical, a rule hint', same: 'muted', ink: true, on: 'card' },
        { token: 'faint', label: 'A column with no answer, a control resting', same: 'text-muted-color', ink: true, on: 'card' },
        { token: 'placeholder', label: 'The hint inside an empty field', same: 'muted', ink: true, on: 'surface' },
        { token: 'heading', label: 'The marketing pages and account forms', same: 'accent', ink: true, on: 'surface' },
        { token: 'title', label: 'A screen or panel title in the product', same: 'heading-color', ink: true, on: 'card' },
        { token: 'chrome-ink', label: 'Nav items, the footer icons', same: 'title', ink: true, on: 'chrome' },
        // The bars a shade darker than the page, one token for both (Tim, 2026-10-11). A key and
        // not a hex: a sheet that states no chrome must darken ITS surface, not paint the built-in
        // blue over a Rosé Pine page, which a hex here did.
        { token: 'chrome', label: 'The nav bar\'s and the footer\'s ground', same: 'surface>5' },
        { token: 'accent', label: 'The current section, an active turn, icons', same: 'primary-color', ink: true, on: 'surface' },
        { token: 'accent-hover', label: 'A link under the pointer', same: 'primary-hover-color', ink: true, on: 'surface' },
        { token: 'link', label: 'The sheet and builder links', same: 'accent', ink: true, on: 'card' },
        { token: 'link-hover', label: 'Those links under the pointer', same: 'accent-hover', ink: true, on: 'card' },
        { token: 'danger-hover', label: 'A dangerous link under the pointer', same: { light: 'danger>15', dark: 'danger<15' }, ink: true, on: 'card' },
    ] },
    { name: 'Lines and focus', tokens: [
        { token: 'edge', label: 'A panel\'s own edge', same: 'content-border-color' },
        { token: 'rule', label: 'A line inside a panel: a table row, a footer', same: 'divider-color' },
        // Clear on purpose: a structural nothing, not a color any role owns.
        { token: 'field', label: 'The ground of a hand-built form field', light: '#00000000', dark: '#00000000' },
        { token: 'field-edge', label: 'A form field at rest', same: 'edge' },
        { token: 'focus', label: 'Where the keyboard is', same: 'focus-ring-color' },
    ] },
    { name: 'Grounds', tokens: [
        { token: 'well', label: 'A box in a card: the game clock, table heads', same: 'content-hover-background' },
        { token: 'well-hover', label: 'That box under the pointer', same: { light: 'well>4', dark: 'well<6' } },
        { token: 'well-raised', label: 'The sheet\'s ability and stat boxes', same: 'well' },
        { token: 'row-hover', label: 'A clickable row under the pointer', same: 'well' },
        { token: 'hover-wash', label: 'A small control under the pointer', same: 'well' },
        { token: 'code', label: 'Inline code and code blocks, a meter\'s track', same: 'rule' },
        { token: 'table-head', label: 'The header row of a list', same: 'well' },
        { token: 'table-row-hover', label: 'A list row under the pointer', same: 'row-hover' },
        { token: 'danger-wash', label: 'A delete control under the pointer', same: 'danger-background' },
        // White ink sits on the fills, so by night the severity goes darker, not lighter.
        { token: 'danger-fill', label: 'A delete button', same: { light: 'danger', dark: 'danger>40' } },
        { token: 'danger-fill-hover', label: 'A delete button under the pointer', same: 'danger-fill>15' },
        { token: 'disabled-fill', label: 'A solid button that cannot be pressed yet', same: 'edge' },
        { token: 'easel', label: 'The easel a screenshot sits on', same: 'well' },
        { token: 'easel-edge', label: 'The easel\'s edge, and its grid', same: 'faint' },
    ] },
    { name: 'Buttons', tokens: [
        { token: 'button-primary', label: 'The hero\'s first button', same: 'button-submit' },
        { token: 'button-secondary', label: 'The hero\'s second button, until it went outlined; nothing reads it', same: 'danger-fill' },
        { token: 'button-submit', label: 'Register, login, contact, join', same: { light: 'success', dark: 'success>40' } },
        { token: 'button-submit-hover', label: 'Those under the pointer', same: 'button-submit>15' },
        { token: 'button-outline-edge', label: 'An outlined button\'s edge', same: 'accent/60' },
        { token: 'button-outline-edge-hover', label: 'That edge under the pointer', same: 'accent' },
        { token: 'button-outline-hover', label: 'Outlined button, hovered', same: 'accent/10' },
        { token: 'social-button', label: 'Continue with Google', same: 'card' },
    ] },
    { name: 'Navigation', tokens: [
        { token: 'nav-sticky', label: 'The bar once it sticks to the top', same: 'chrome/85' },
        { token: 'nav-disc', label: 'The round palette and full-screen buttons', same: 'well' },
        { token: 'nav-disc-hover', label: 'Those under the pointer', same: 'well-hover' },
        { token: 'nav-glyph', label: 'The palette and full-screen glyphs', same: { light: 'accent>10', dark: 'accent<10' }, ink: true, on: 'nav-disc' },
        { token: 'nav-menu-edge', label: 'The color menu\'s border', same: 'edge' },
    ] },
    // A band's ink goes a quarter of the way to the ink end of its severity: darker by day,
    // lighter by night, so it reads on the severity's own tint.
    { name: 'Home', tokens: [
        { token: 'hero-title', label: 'The hero\'s headline', same: 'chart-3', ink: true, on: 'surface' },
        { token: 'roadmap-month', label: 'A roadmap month band', same: 'well' },
        { token: 'roadmap-rule', label: 'The line between roadmap months', same: 'edge' },
        { token: 'roadmap-magenta', label: 'Beta 1 band', same: 'warn-background' },
        { token: 'roadmap-magenta-ink', label: 'Beta 1 name', same: { light: 'warn>25', dark: 'warn<25' }, ink: true, on: 'roadmap-magenta' },
        { token: 'roadmap-red', label: 'Beta 2 band', same: 'danger-wash' },
        { token: 'roadmap-red-ink', label: 'Beta 2 name', same: 'title', ink: true, on: 'roadmap-red' },
        { token: 'roadmap-red-text', label: 'Beta 2 release text', same: 'danger-hover', ink: true, on: 'roadmap-red' },
        { token: 'roadmap-red-date', label: 'Beta 2 dates', same: 'danger-hover', ink: true, on: 'roadmap-red' },
        { token: 'roadmap-green', label: 'Beta 3 band', same: 'success-background' },
        { token: 'roadmap-green-ink', label: 'Beta 3 name', same: { light: 'success>25', dark: 'success<25' }, ink: true, on: 'roadmap-green' },
        { token: 'roadmap-cyan', label: 'Virtual Beta 1 band', same: 'info-background' },
        { token: 'roadmap-cyan-ink', label: 'Virtual Beta 1 name', same: { light: 'info>25', dark: 'info<25' }, ink: true, on: 'roadmap-cyan' },
        { token: 'roadmap-purple', label: 'Virtual Beta 2 band', same: 'help-background' },
        { token: 'roadmap-purple-ink', label: 'Virtual Beta 2 name', same: { light: 'help>25', dark: 'help<25' }, ink: true, on: 'roadmap-purple' },
        { token: 'roadmap-sky', label: 'Virtual Beta 3 band', same: 'highlight-background' },
        { token: 'roadmap-sky-ink', label: 'Virtual Beta 3 name', same: 'title', ink: true, on: 'roadmap-sky' },
        { token: 'goal-met', label: 'A finances goal met', same: 'success' },
    ] },
    { name: 'Codex', sees: { flags: [ FLAG_ROLLOUT_CODEX ] }, tokens: [
        { token: 'codex-heading', label: 'Entry headings and table headers', same: 'hero-title', ink: true, on: 'surface' },
        { token: 'codex-link', label: 'An entry\'s title and links in the contents', same: 'accent', ink: true, on: 'surface' },
        { token: 'codex-chip', label: 'An entry chip on the contents page', same: 'card' },
        { token: 'codex-stripe', label: 'Every other row of an entry\'s table', same: 'well' },
        { token: 'codex-tab', label: 'The codex toolbar and comments button', same: 'well' },
        { token: 'codex-tab-edge', label: 'The codex toolbar\'s edge', same: 'edge' },
    ] },
    { name: 'Console', sees: { signedIn: true, flags: [ FLAG_ROLLOUT_CONSOLE ] }, tokens: [
        { token: 'hp-well', label: 'Hit points: unhurt', same: 'success', ink: true, on: 'card' },
        { token: 'hp-hurt', label: 'Hit points: hurt', same: 'warn', ink: true, on: 'card' },
        { token: 'hp-bloodied', label: 'Hit points: bloodied', same: 'chart-2', ink: true, on: 'card' },
        { token: 'hp-down', label: 'Hit points: down', same: 'danger', ink: true, on: 'card' },
        { token: 'game-tab-bar', label: 'The bar under the active tab', same: 'accent' },
        { token: 'game-row-edge', label: 'Whose turn it is', same: 'accent' },
        { token: 'game-row-picked', label: 'The row the tab on top is aimed at', same: 'highlight-background' },
        // A screen's tab underline and card edge; the tab's name is the ink.
        { token: 'screen-console', label: 'The Console screen', same: 'accent', ink: true, on: 'card' },
        { token: 'screen-character', label: 'The Character screen', same: 'success', ink: true, on: 'card' },
        { token: 'screen-codex', label: 'The Codex screen', same: 'hero-title', ink: true, on: 'card' },
        { token: 'screen-map', label: 'The Map screen', same: 'warn', ink: true, on: 'card' },
        { token: 'screen-chat', label: 'The Chat screen', same: 'help', ink: true, on: 'card' },
        { token: 'screen-gm', label: 'The GM\'s screen', same: 'danger', ink: true, on: 'card' },
        { token: 'screen-notes', label: 'The Notes screen', same: 'info', ink: true, on: 'card' },
        { token: 'die-d4', label: 'The d4', same: 'chart-1' },
        { token: 'die-d6', label: 'The d6', same: 'chart-2' },
        { token: 'die-d8', label: 'The d8', same: 'chart-4' },
        { token: 'die-d10', label: 'The d10', same: 'chart-5' },
        { token: 'die-d12', label: 'The d12', same: 'chart-6' },
        { token: 'die-d20', label: 'The d20', same: 'chart-3' },
        { token: 'die-d100', label: 'The d100', same: 'chart-8' },
        // A spell slot's level, rainbow order and on past violet; the fill, a spent pip's ring, and its
        // digit, which is the card's color on the fill -- the same pair, so one ratio holds both.
        // Indigo is the one hue the categories lack, so slot 6 mixes blue and violet. By day red,
        // orange, and yellow darken to the same depth to clear contrast on the card, and orange sat
        // between its neighbors (T24), so it is darker still there: told apart by depth, not hue.
        { token: 'slot-1', label: 'A level 1 spell slot (red)', same: 'chart-1', ink: true, on: 'card' },
        { token: 'slot-2', label: 'A level 2 spell slot (orange)', same: { light: 'chart-2>40', dark: 'chart-2' }, ink: true, on: 'card' },
        { token: 'slot-3', label: 'A level 3 spell slot (yellow)', same: 'chart-3', ink: true, on: 'card' },
        { token: 'slot-4', label: 'A level 4 spell slot (green)', same: 'chart-4', ink: true, on: 'card' },
        { token: 'slot-5', label: 'A level 5 spell slot (blue)', same: 'chart-5', ink: true, on: 'card' },
        { token: 'slot-6', label: 'A level 6 spell slot (indigo)', same: 'chart-5+chart-6', ink: true, on: 'card' },
        { token: 'slot-7', label: 'A level 7 spell slot (violet)', same: 'chart-6', ink: true, on: 'card' },
        { token: 'slot-8', label: 'A level 8 spell slot (magenta)', same: 'chart-7', ink: true, on: 'card' },
        { token: 'slot-9', label: 'A level 9 spell slot (silver)', same: 'chart-8', ink: true, on: 'card' },
    ] },
    { name: 'Chat', sees: { signedIn: true, flags: [ FLAG_ROLLOUT_CONSOLE ] }, tokens: [
        { token: 'chat-say', label: 'What somebody said', same: 'body', ink: true, on: 'card' },
        { token: 'chat-system', label: 'The session starting and ending', same: 'help', ink: true, on: 'card' },
        { token: 'chat-turn', label: 'Whose turn, and a new round', same: 'warn', ink: true, on: 'card' },
        { token: 'chat-success', label: 'A roll that passed', same: 'success', ink: true, on: 'card' },
        { token: 'chat-failure', label: 'A roll that failed', same: 'danger', ink: true, on: 'card' },
        { token: 'chat-who', label: 'Who said it', same: 'success', ink: true, on: 'card' },
        { token: 'chat-mark', label: 'The time and the marks', same: 'muted', ink: true, on: 'card' },
        { token: 'chat-staff', label: 'An admin\'s or an alpha\'s name in the Commons', same: 'warn', ink: true, on: 'card' },
        { token: 'chat-subscriber', label: 'A subscriber\'s name in the Commons', same: 'info', ink: true, on: 'card' },
        { token: 'topic-everyone', label: 'An Everyone topic: its pill and its row\'s edge', same: 'info', ink: true, on: 'card' },
        { token: 'topic-gms', label: 'A GMs topic', same: 'help', ink: true, on: 'card' },
        { token: 'topic-campaign', label: 'A table\'s topic', same: 'success', ink: true, on: 'card' },
        { token: 'topic-friends', label: 'A Friends topic', same: 'warn', ink: true, on: 'card' },
        { token: 'topic-bot', label: 'A GM\'s private topic with the bot', same: 'muted', ink: true, on: 'card' },
    ] },
    { name: 'Campaigns and characters',
        sees: { signedIn: true, flags: [ FLAG_ROLLOUT_CONSOLE, FLAG_ROLLOUT_CHARACTERS ] }, tokens: [
        { token: 'role-gm', label: 'A campaign card you GM', same: 'chart-6' },
        { token: 'role-player', label: 'A campaign card you play in', same: 'chart-4' },
        { token: 'role-observer', label: 'A campaign card you watch', same: 'chart-2' },
        { token: 'badge', label: 'A roster badge', same: 'highlight-background' },
        { token: 'badge-ink', label: 'Its text', same: 'accent-hover', ink: true, on: 'badge' },
        { token: 'builder-picked', label: 'The builder\'s chosen option', same: 'accent' },
        { token: 'notice', label: 'An account notice', same: 'warn-background' },
        { token: 'notice-edge', label: 'Its edge', same: 'warn' },
        { token: 'notice-ink', label: 'Its text', same: { light: 'warn>25', dark: 'warn<25' }, ink: true, on: 'notice' },
    ] },
    { name: 'Admin', sees: { admin: true }, tokens: [
        { token: 'admin-link', label: 'Links and sort arrows in the admin lists', same: 'accent', ink: true, on: 'card' },
        { token: 'admin-link-hover', label: 'Those under the pointer', same: 'accent-hover', ink: true, on: 'card' },
        { token: 'admin-ring', label: 'An active filter, a focused name', same: 'focus-ring-color' },
        { token: 'history-alert', label: 'History: alert channel', same: 'help-background' },
        { token: 'history-alert-ink', label: 'Its text', same: { light: 'help>25', dark: 'help<25' }, ink: true, on: 'history-alert' },
        { token: 'history-app', label: 'History: app channel', same: 'well' },
        { token: 'history-app-ink', label: 'Its text', same: 'body', ink: true, on: 'history-app' },
        { token: 'history-info', label: 'History: info', same: 'info-background' },
        { token: 'history-info-ink', label: 'Its text', same: { light: 'info>25', dark: 'info<25' }, ink: true, on: 'history-info' },
        { token: 'history-warn', label: 'History: warning', same: 'notice' },
        { token: 'history-warn-ink', label: 'Its text', same: 'notice-ink', ink: true, on: 'history-warn' },
        { token: 'history-error', label: 'History: error', same: 'danger-wash' },
        { token: 'history-error-ink', label: 'Its text', same: 'danger-hover', ink: true, on: 'history-error' },
        { token: 'metric-a', label: 'Metrics: the first series', same: 'accent' },
        { token: 'metric-b', label: 'Metrics: the second', same: 'chart-5' },
        { token: 'metric-c', label: 'Metrics: the third', same: 'chart-6' },
        { token: 'metric-d', label: 'Metrics: the fourth', same: 'chart-4' },
        { token: 'metric-target', label: 'Metrics: a plan or target line', same: 'chart-3' },
        { token: 'metric-good', label: 'Metrics: a diagnostic on target', same: 'success', ink: true, on: 'surface' },
        { token: 'metric-watch', label: 'Metrics: one just under', same: 'warn', ink: true, on: 'surface' },
        { token: 'metric-warn', label: 'Metrics: one well under', same: 'danger', ink: true, on: 'surface' },
    ] },
];

/** The least contrast an ink may have on its ground: WCAG AA for normal text. */
export const ES_CONTRAST_MIN = 4.5;

/**
 * Whether a viewer is shown a group. pViewer is { all, signedIn, admin, flags },
 * flags keyed by flag key; `all` is an admin, whose edits are the site's defaults.
 */
export const esSeesGroup = ( pGroup, pViewer ) => {
    const aSees = pGroup.sees;
    if ( !aSees || pViewer.all ) return true;
    if ( aSees.admin && !pViewer.admin ) return false;
    if ( aSees.signedIn && !pViewer.signedIn ) return false;
    return !aSees.flags || aSees.flags.some( ( aKey ) => pViewer.flags?.[ aKey ] );
};

export const ES_TOKENS = ES_TOKEN_GROUPS.flatMap( ( pGroup ) => pGroup.tokens );

export const ES_TOKEN_NAMES = ES_TOKENS.map( ( pToken ) => pToken.token );

const BY_NAME = Object.fromEntries( ES_TOKENS.map( ( pToken ) => [ pToken.token, pToken ] ) );

export const esToken = ( pName ) => BY_NAME[ pName ] ?? null;

/** The custom property a token is. One namespace, so a utility exists for each. */
export const esTokenVar = ( pName ) => '--color-' + pName;

const HEX = /^#([0-9a-f]{6}|[0-9a-f]{8})$/;

/**
 * The one shape a saved value takes: `#rrggbb`, or `#rrggbbaa` with alpha.
 * Mirrors EsThemeTokens.isValue on esback. Anything else is null.
 */
export const esAsHex = ( pValue ) => {
    if ( pValue == null ) return null;
    const aValue = String( pValue ).trim().toLowerCase();
    if ( HEX.test( aValue ) ) return aValue;
    if ( aValue === 'white' ) return '#ffffff';
    if ( aValue === 'black' ) return '#000000';

    const aMatch = /^([a-z]+)-(\d+)$/.exec( aValue );
    if ( !aMatch ) return null;
    const aAt = ES_SHADES.indexOf( Number( aMatch[ 2 ] ) );
    return ES_PALETTE[ aMatch[ 1 ] ]?.[ aAt ] ?? null;
};

/** A token's key in pMode, as a string: `same` itself, or its pMode half. Null for none. */
export const esTokenKey = ( pToken, pMode ) => {
    const aSame = pToken?.same;
    return ( typeof aSame === 'string' ? aSame : aSame?.[ pMode ] ) ?? null;
};

/**
 * A key taken apart: `accent/60` is { base: 'accent', alpha: 60 }, `surface>5` is
 * { base: 'surface', shade: 5 }, `surface<5` { base: 'surface', tint: 5 }, and `danger+warn`
 * { base: 'danger', mix: 'warn' }; a part the key does not state is null.
 */
export const esKey = ( pSame ) => {
    const aMatch = /^([a-z0-9-]+)(?:\/(\d{1,3})|>(\d{1,3})|<(\d{1,3})|\+([a-z0-9-]+))?$/.exec( pSame ?? '' );
    const aNumber = ( pText ) => ( pText == null ? null : Number( pText ) );
    return aMatch ? {
        base: aMatch[ 1 ], alpha: aNumber( aMatch[ 2 ] ), shade: aNumber( aMatch[ 3 ] ), tint: aNumber( aMatch[ 4 ] ),
        mix: aMatch[ 5 ] ?? null,
    } : null;
};

/** pHex at pAlpha percent, as `#rrggbbaa`; an alpha the key does not state leaves it alone. */
const atAlpha = ( pHex, pAlpha ) => ( pHex == null || pAlpha == null ? pHex
    : pHex.slice( 0, 7 ) + Math.round( pAlpha * 255 / 100 ).toString( 16 ).padStart( 2, '0' ) );

/**
 * pHex mixed pAt percent toward pToward, as color-mix( in srgb ) paints it: each channel
 * moved, rounded. pHex's alpha is kept. A percent the key does not state leaves it alone.
 */
const toward = ( pHex, pToward, pAt ) => ( pHex == null || pToward == null || pAt == null ? pHex
    : '#' + [ 1, 3, 5 ].map( ( pFrom ) => {
        const aChannel = ( pColor ) => parseInt( pColor.slice( pFrom, pFrom + 2 ), 16 );
        return Math.round( aChannel( pHex ) + ( aChannel( pToward ) - aChannel( pHex ) ) * pAt / 100 )
            .toString( 16 ).padStart( 2, '0' );
    } ).join( '' ) + pHex.slice( 7 ) );

// Keys chain, so a wrong table could loop; the test refuses a cycle and this stops one.
const DEPTH = 8;

/**
 * What a token paints with pColors in force -- a merged layer map, { token: { light, dark } }
 * of hex -- or the built-in when the map holds no such cell. A keyed token follows its base
 * through the map, as its var() does through the stylesheet. Hex, or null for no such token.
 */
export const esResolveColor = ( pColors, pName, pMode, pDepth = DEPTH ) => {
    const aToken = esToken( pName );
    if ( !aToken || pDepth === 0 ) return null;
    const aOwn = esAsHex( pColors?.[ pName ]?.[ pMode ] );
    if ( aOwn ) return aOwn;
    const aKey = esKey( esTokenKey( aToken, pMode ) );
    if ( !aKey ) return esAsHex( aToken[ pMode ] );
    let aHex = esResolveColor( pColors, aKey.base, pMode, pDepth - 1 );
    aHex = toward( aHex, '#000000', aKey.shade );
    aHex = toward( aHex, '#ffffff', aKey.tint );
    if ( aKey.mix ) aHex = toward( aHex, esResolveColor( pColors, aKey.mix, pMode, pDepth - 1 ), 50 );
    return atAlpha( aHex, aKey.alpha );
};

/** The panel: the semantic tokens with values of their own, which every other color keys to. */
export const ES_PANEL = ES_TOKEN_GROUPS.find( ( pGroup ) => pGroup.name === 'Panel' ).tokens.map( ( pToken ) => pToken.token );

const reaches = ( pName, pMode, pTarget, pDepth = DEPTH ) => {
    const aKey = esKey( esTokenKey( esToken( pName ), pMode ) );
    if ( !aKey || pDepth === 0 ) return false;
    return [ aKey.base, aKey.mix ].filter( Boolean )
        .some( ( aBase ) => aBase === pTarget || reaches( aBase, pMode, pTarget, pDepth - 1 ) );
};

/**
 * Every cell whose key leads to pName, as [ { token, mode } ]: what a pick of pName moves,
 * and what a cell of its own would cut loose from it.
 */
export const esFollowers = ( pName ) => ES_TOKENS.flatMap( ( pToken ) => [ 'light', 'dark' ]
    .filter( ( aMode ) => reaches( pToken.token, aMode, pName ) )
    .map( ( aMode ) => ( { token: pToken.token, mode: aMode } ) ) );

/** A built-in value as hex, which is what every comparison and preview needs. */
export const esBuiltIn = ( pName, pMode ) => esResolveColor( null, pName, pMode );

// ---------------------------------------------------------------------------
// Contrast -- WCAG 2's relative luminance, alpha composited over the ground.
// ---------------------------------------------------------------------------

const rgba = ( pHex ) => {
    const aHex = esAsHex( pHex );
    if ( !aHex ) return null;
    const aNum = ( pAt ) => parseInt( aHex.slice( pAt, pAt + 2 ), 16 );
    return [ aNum( 1 ), aNum( 3 ), aNum( 5 ), aHex.length === 9 ? aNum( 7 ) / 255 : 1 ];
};

const over = ( pTop, pBottom ) => pTop.slice( 0, 3 ).map(
    ( pChannel, pAt ) => pChannel * pTop[ 3 ] + pBottom[ pAt ] * ( 1 - pTop[ 3 ] ) );

const luminance = ( pRgb ) => {
    const [ aR, aG, aB ] = pRgb.map( ( pChannel ) => {
        const aC = pChannel / 255;
        return aC <= 0.03928 ? aC / 12.92 : ( ( aC + 0.055 ) / 1.055 ) ** 2.4;
    } );
    return 0.2126 * aR + 0.7152 * aG + 0.0722 * aB;
};

/**
 * The contrast ratio of pInk on pGround, 1 to 21, or null for a value that is
 * not a color. pBase is what a translucent ground sits on -- the page.
 */
export const esContrast = ( pInk, pGround, pBase = '#ffffff' ) => {
    const aBase = rgba( pBase );
    const aGround = rgba( pGround );
    const aInk = rgba( pInk );
    if ( !aBase || !aGround || !aInk ) return null;

    const aSolidGround = over( aGround, aBase.slice( 0, 3 ) );
    const aSolidInk = over( aInk, aSolidGround );
    const [ aHi, aLo ] = [ luminance( aSolidInk ), luminance( aSolidGround ) ].sort( ( a, b ) => b - a );
    return ( aHi + 0.05 ) / ( aLo + 0.05 );
};

/**
 * The contrast of an ink token on its ground, with pColors in force, in pMode: the ground
 * sits on that mode's page. Null for a token that is not an ink.
 */
export const esTokenContrast = ( pColors, pName, pMode ) => {
    const aToken = esToken( pName );
    if ( !aToken?.ink ) return null;
    return esContrast( esResolveColor( pColors, pName, pMode ), esResolveColor( pColors, aToken.on, pMode ),
        esResolveColor( pColors, 'surface', pMode ) );
};
